import React from 'react';
import { useLocation } from 'react-router-dom';
import { gtmId } from '../config';
import { useUserState } from '../contexts/UserContext';
import { CustomEnvironmentParams, GTMEvent, GtmPurchaseEvent, GtmVirtualPageView, SnippetParams } from '../models/GoogleTagManager';
import { SubscriptionProduct } from '../models/SubscriptionProduct';
import { User } from '../models/User';

export const DEFAULT_DOMAIN = 'https://www.googletagmanager.com'

const getDataLayerSnippet = () => 'window.dataLayer = window.dataLayer || [];'

const getIframeSnippet = (
  id: SnippetParams['id'],
  environment?: CustomEnvironmentParams
) => {
  let params = '';
  if (environment) {
    const { gtm_auth, gtm_preview } = environment
    params = `&gtm_auth=${gtm_auth}&gtm_preview=${gtm_preview}&gtm_cookies_win=x`
  }
  return `<iframe src="${DEFAULT_DOMAIN}/ns.html?id=${id}${params}" height="0" width="0" style="display:none;visibility:hidden" id="tag-manager"></iframe>`
}

const getGTMScript = (
  id: SnippetParams['id'],
  environment?: CustomEnvironmentParams
) => {
  let params = ''
  if (environment) {
    const { gtm_auth, gtm_preview } = environment;
    params = `+"&gtm_auth=${gtm_auth}&gtm_preview=${gtm_preview}&gtm_cookies_win=x"`
  }
  return `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      '${DEFAULT_DOMAIN}/gtm.js?id='+i+dl${params};f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${id}');
  `
}

const setupGTM = (params: SnippetParams) => {
  const getDataLayerScript = (): HTMLElement => {
    const dataLayerScript = document.createElement('script');
    dataLayerScript.innerHTML = getDataLayerSnippet();
    return dataLayerScript;
  }

  const getNoScript = (): HTMLElement => {
    const noScript = document.createElement('noscript');
    noScript.innerHTML = getIframeSnippet(params.id, params.environment);
    return noScript;
  }

  const getScript = (): HTMLElement => {
    const script = document.createElement('script');
    script.innerHTML = getGTMScript(params.id, params.environment);
    return script;
  }

  return {
    getDataLayerScript,
    getNoScript,
    getScript
  }
}

const init = (environment?: CustomEnvironmentParams) => {
  if (gtmId) {
    const gtm = setupGTM({ id: gtmId, environment });

    const dataLayerScript = gtm.getDataLayerScript();
    const script = gtm.getScript();
    const noScript = gtm.getNoScript();

    document.head.insertBefore(dataLayerScript, document.head.childNodes[0]!);
    document.head.insertBefore(script, document.head.childNodes[1]!);
    document.body.insertBefore(noScript, document.body.childNodes[0]!);
  }
}

const sendEvent = (data: GtmPurchaseEvent | GtmVirtualPageView | { ecommerce: null }) => {
  if (window.dataLayer) {
    window.dataLayer.push(data);
  } else {
    console.warn(`[GTMService] dataLayer does not exist.`);
  }
}

const calcTax = (grossPLN: number): number =>
  Math.round((grossPLN * 23) / 123 * 100) / 100;

export interface PurchaseEventInfo {
  product: SubscriptionProduct;
  user?: User;
  email?: string;
  coupon?: string;
  transactionId?: string;
  customerType?: 'new' | 'returning';
  paymentType?: string;
  effectivePrice?: number;
}

const pushPurchase = (info: PurchaseEventInfo) => {
  const sub = info.user?.subscription;
  const effectivePrice = info.effectivePrice ?? sub?.currentPrice ?? info.product.price
  const emailAddr = info.user?.email ?? info.email;
  const grossPLN = (effectivePrice ?? info.product.price) / 100;
  const transactionId = info.transactionId;
  if (!transactionId) {
    return;
  }
  sendEvent({ ecommerce: null });
  sendEvent({
    event: GTMEvent.Purchase,
    ecommerce: {
      transaction_id: transactionId,
      value: grossPLN,
      tax: calcTax(grossPLN),
      currency: 'PLN',
      coupon: info.coupon,
      payment_type: info.paymentType,
      user_data: emailAddr
        ? { email: emailAddr, customer_type: info.customerType ?? 'new' }
        : undefined,
      items: [{
        item_id: info.product.id,
        item_name: info.product.name,
        price: grossPLN,
      }],
    },
  });
};

const GTMService = {
  init,
  sendEvent,
  pushPurchase,
};

export function useVirtualPageView() {
  const location = useLocation();
  const userState = useUserState();
  const previousUrlRef = React.useRef(document.referrer);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      GTMService.sendEvent({
        event: GTMEvent.VirtualPageView,
        user_status: userState.isLoggedIn ? 'Logged-in' : 'Logged-out',
        user_id: userState.user.id,
        page_location: window.location.href,
        page_title: document.title,
        page_referrer: previousUrlRef.current,
      });
      previousUrlRef.current = window.location.href;
    });
    return () => clearTimeout(timeoutId);
  }, [location, userState]);
}

export default GTMService;
